import React, { useState } from 'react';
import { FaRupeeSign, FaChartLine, FaCalendar, FaInfoCircle, FaShieldAlt, FaQuestionCircle, FaGem, FaMoneyBillWave, FaClock, FaChartPie, FaCalculator, FaChartArea } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';

const PersonalLoanEMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTenure, setLoanTenure] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const calculateEMI = () => {
    if (!loanAmount || !interestRate || !loanTenure) {
      setError('Please fill in all required fields');
      return;
    }

    const P = parseFloat(loanAmount);
    const r = parseFloat(interestRate) / 12 / 100;
    const n = parseFloat(loanTenure) * 12;

    if (isNaN(P) || isNaN(r) || isNaN(n)) {
      setError('Please enter valid numbers');
      return;
    }

    // EMI calculation using formula: EMI = P * r * (1 + r)^n / [(1 + r)^n - 1]
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalInterest = (emi * n) - P;
    const totalPayment = emi * n;

    const resultData = {
      emi: emi.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      totalPayment: totalPayment.toFixed(2),
      monthlyInterest: ((P * r) / 12).toFixed(2)
    };

    setResult(resultData);
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const chartOptions = {
    chart: {
      type: 'donut',
      height: 400,
      toolbar: {
        show: false
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    colors: ['#FFB703', '#FF8500'],
    labels: ['Principal Amount', 'Interest Amount'],
    legend: {
      position: 'bottom',
      fontSize: '14px',
      fontFamily: 'Inter, sans-serif',
      labels: {
        colors: '#4B5563'
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }],
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        colors: ['#4B5563']
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%'
        }
      }
    }
  };

  const chartSeries = result ? [parseFloat(loanAmount), parseFloat(result.totalInterest)] : [];

  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-50 min-h-screen p-8">
      <Helmet>
        <title>Personal Loan EMI Calculator</title>
        <meta name="description" content="Calculate your personal loan EMI and understand your repayment schedule." />
        <meta property="og:title" content="Personal Loan EMI Calculator" />
        <meta property="og:description" content="Calculate your personal loan EMI and understand your repayment schedule." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-4xl font-bold text-gray-800 flex items-center">
            <FaGem className="mr-3 text-4xl text-blue-600" />
            Personal Loan EMI Calculator
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-blue-700 text-sm font-bold mb-3 flex items-center">
              <FaMoneyBillWave className="inline-block mr-2 text-blue-500" />
              Loan Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" />
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
                placeholder="Enter loan amount"
                min="0"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-blue-700 text-sm font-bold mb-3 flex items-center">
              <FaChartPie className="inline-block mr-2 text-blue-500" />
              Interest Rate (% p.a.)
            </label>
            <input
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              className="w-full p-3 border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
              placeholder="Enter interest rate"
              min="0"
              step="0.01"
            />
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-blue-700 text-sm font-bold mb-3 flex items-center">
              <FaCalendar className="inline-block mr-2 text-blue-500" />
              Loan Tenure (Years)
            </label>
            <input
              type="number"
              value={loanTenure}
              onChange={(e) => setLoanTenure(e.target.value)}
              className="w-full p-3 border border-blue-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300"
              placeholder="Enter loan tenure"
              min="0"
            />
          </div>
        </div>

        <button
          onClick={calculateEMI}
          className="w-full bg-gradient-to-r from-green-600 to-purple-600 hover:from-green-700 hover:to-purple-700 text-red font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-10"
        >
          <FaCalculator className="inline-block mr-2" />
          Calculate EMI
        </button>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartArea className="mr-3 text-blue-600" />
              Loan Breakdown
            </h2>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <Chart options={chartOptions} series={chartSeries} type="donut" height={400} />
            </div>
            <div className="mt-6 p-6 bg-blue-50 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Breakdown Details</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Principal Amount</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrency(loanAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Interest Amount</span>
                  <span className="font-semibold text-red-600">
                    {formatCurrency(result.totalInterest)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Repayment</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrency(result.totalPayment)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartLine className="mr-3 text-blue-600" />
              EMI Details
            </h2>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Monthly EMI</span>
                <span className="font-semibold text-blue-600">
                  {formatCurrency(result.emi)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Interest Payable</span>
                <span className="font-semibold text-red-600">
                  {formatCurrency(result.totalInterest)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Amount Payable</span>
                <span className="font-semibold text-blue-600">
                  {formatCurrency(result.totalPayment)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Monthly Interest</span>
                <span className="font-semibold text-blue-600">
                  {formatCurrency(result.monthlyInterest)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfoCircle className="mr-3 text-blue-600" />
            About Personal Loans
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-blue-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-blue-700">Key Features</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>No collateral required</li>
                <li>Quick approval process</li>
                <li>Flexible repayment options</li>
                <li>Multiple usage options</li>
              </ul>
            </div>
            <div className="p-6 bg-blue-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-blue-700">Benefits</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Instant access to funds</li>
                <li>No end-use restrictions</li>
                <li>Transparent terms and conditions</li>
                <li>Competitive interest rates</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaQuestionCircle className="mr-3 text-blue-600" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">What is a personal loan?</h3>
              <p className="text-gray-600">
                A personal loan is an unsecured loan that can be used for various personal expenses.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">What factors affect interest rates?</h3>
              <p className="text-gray-600">
                Credit score, income, employment history, and loan amount affect interest rates.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">How can I reduce my EMI?</h3>
              <p className="text-gray-600">
                Choose a longer tenure or negotiate a lower interest rate with your lender.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfoCircle className="mr-3 text-blue-600" />
            How to Use
          </h2>
          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Step 1: Enter Loan Amount</h3>
              <p className="text-gray-600">
                Input the total amount you wish to borrow as a personal loan.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Step 2: Set Interest Rate</h3>
              <p className="text-gray-600">
                Enter the annual interest rate offered by your lender (usually between 10-18%).
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Step 3: Enter Loan Tenure</h3>
              <p className="text-gray-600">
                Specify the number of years you want to repay the loan (typically 1-5 years).
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-blue-700">Step 4: Calculate EMI</h3>
              <p className="text-gray-600">
                Click the "Calculate EMI" button to see your monthly EMI, total interest, and repayment schedule.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalLoanEMICalculator;
